import React from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { LoadingContainer } from '../LoadingContainer'
import {
  resolveBrandLogo,
  resolvePrimaryColor,
} from '../PurchaseFlow/utils/branding'
import {
  getCachedBrandingForPath,
  isBrandedLoaderPath,
} from '../../utilities/orgBrandingCache'

import logoMark from '../../assets/logo.svg'

import './pageLoadingContainer.scss'

/**
 * Full-page loading state.
 * On branded storefront/purchase transitions, uses the tenant BrandLoader
 * (org logo + primaryColor) when branding is available via props or cache.
 */
export default function PageLoadingContainer({
  text = 'Loading...',
  subtext,
  icon,
  organization,
  accent,
  logoSrc,
  name,
  message = 'loading blocktickets',
}) {
  const location = useLocation()
  const params = useParams()

  let branding = null

  if (organization) {
    branding = {
      name: name || organization.name || null,
      primaryColor: accent || resolvePrimaryColor(null, organization),
      logoSrc: logoSrc || resolveBrandLogo(null, organization),
    }
  } else if (accent || logoSrc || name) {
    branding = {
      name: name || null,
      primaryColor: accent || null,
      logoSrc: logoSrc || null,
    }
  } else if (isBrandedLoaderPath(location.pathname)) {
    branding = getCachedBrandingForPath(location.pathname, params)
  }

  const tenantColor = branding?.primaryColor || null
  const tenantLogo = branding?.logoSrc || null
  const tenantName = branding?.name || null
  const useTenant = Boolean(tenantColor && (tenantLogo || tenantName))

  if (useTenant) {
    return (
      <div
        id="page-loading-container"
        className="page-loading-container--tenant"
        style={{ '--loader-accent': tenantColor }}
        role="status"
        aria-label="Loading"
        aria-busy="true"
      >
        <div className="page-loading-container__glow" aria-hidden />

        <div className="page-loading-container__mark">
          <svg
            className="page-loading-container__ring"
            viewBox="0 0 120 120"
            aria-hidden
          >
            <circle
              cx="60"
              cy="60"
              r="55"
              fill="none"
              stroke="rgba(255,255,255,0.18)"
              strokeWidth="3"
            />
            <circle
              cx="60"
              cy="60"
              r="55"
              fill="none"
              stroke="#ffffff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="86 260"
            />
          </svg>
          <div className="page-loading-container__logo-disk">
            {tenantLogo ? (
              <img
                src={tenantLogo}
                alt={tenantName || ''}
                className="page-loading-container__logo"
              />
            ) : (
              <span className="page-loading-container__logo-fallback">
                {tenantName?.charAt(0) || 'B'}
              </span>
            )}
          </div>
        </div>

        <div className="page-loading-container__copy">
          {tenantName && (
            <div className="page-loading-container__name">{tenantName}</div>
          )}
          <div className="page-loading-container__message">{message}</div>
        </div>

        <div className="page-loading-container__powered">
          <img src={logoMark} alt="Blocktickets" />
        </div>
      </div>
    )
  }

  return (
    <div id="page-loading-container">
      <LoadingContainer>
        <div className="loading-text-container">
          <h1 className={`loading-text loading-text--${icon}`}>{text}</h1>
        </div>
        {subtext && <p className="loading-text-caption">{subtext}</p>}
      </LoadingContainer>
    </div>
  )
}
